import log from 'loglevel';

import type { Thunk } from '~/store';
import type Api from '~/types';
import type { ClientStateChangeEvent } from '~/typings/client';
import { CLIENT_ACTIONS } from '~/constants';

import { showBlueprintsViewer } from '~/store/dialogs';
import {
    setAnimationState as appSliceSetAnimationState,
    setBrowserIsReady as appSliceSetBrowserIsReady,
    setRepeatingState as appSliceSetRepeatingState,
    setShipLevel as appSliceSetShipLevel,
    setWorldRendererState as appSliceSetWorldRendererState,
    switchSync as appSliceSwitchSync,
    updateAnimationSubtitle as appSliceUpdateAnimationSubtitle,
    updateClientState as appSliceUpdateClientState,
} from '~/store/app';
import ClientApi from '~/clientApi';
import { getClientState, getNewExtraShipLevel } from '~/utils/account';
import animationInterrupter from '~/utils/animationInterrupter';
import animationIterator from '~/utils/animationIterator';
import { dropProgressSpeed, timing, updateProgressSpeed } from '~/utils/updateProgressSpeed';


export const browserIsReady = (vol?: number): Thunk => (dispatch) => {
    const volume = vol !== undefined && vol > 0 ? vol : 0;

    dispatch(appSliceSetBrowserIsReady({
        volume,
    }));
};


export const setClientStateListener = (): Thunk => (dispatch, getState) => {
    window.onClientStateChange = (data: string) => {
        const state = getState();
        const event: ClientStateChangeEvent = JSON.parse(data);
        switch (event.actionId) {
            case CLIENT_ACTIONS.START_ANIMATION: {
                if (ClientApi.getWaitingList().includes(event.level)) {
                    dispatch(appSliceSwitchSync(false));
                    timing.setStartTime(Date.now(), event.level);
                    updateProgressSpeed(event.level, state.app.timings);
                    dispatch(appSliceSetAnimationState(true));
                    dispatch(updateAnimationSubtitle(event.level, false));
                }
                break;
            }

            case CLIENT_ACTIONS.FINISH_ANIMATION: {
                if (ClientApi.getWaitingList().includes(event.level)) {
                    timing.setEndTime(Date.now(), event.level);
                    animationInterrupter(getState, event.level).then(() => {
                        animationIterator.animationFinished(event.level);
                        dropProgressSpeed(true);
                        if (state.app.shipLevel === event.level) {
                            dispatch(appSliceSetAnimationState(false));
                            dispatch(appSliceSetRepeatingState(false));
                            dispatch(appSliceSwitchSync(true));
                        }
                    });
                }
                break;
            }
            case 'browserShown': {
                dispatch(browserIsReady(event.browserVolume));
                break;
            }
            default:
        }
    };
};


export const updateAnimationSubtitle = (
    level: number,
    isInstant: boolean,
    clb?: () => void
): Thunk => (dispatch, getState) => {
    const { shipLevels, blueprintsCount } = getState().app;
    const { isBlueprintsViewerVisible } = getState().dialogs;
    let animationStateSubtitle: string | undefined | null = '';

    shipLevels.forEach((shipLevel) => {
        if (shipLevel.level === level) {
            animationStateSubtitle = shipLevel.description;
        }
    });

    dispatch(appSliceUpdateAnimationSubtitle({
        animationStateSubtitle,
        level,
        isInstant,
    }));
    if (blueprintsCount && level <= blueprintsCount && !isBlueprintsViewerVisible) {
        dispatch(showBlueprintsViewer());
    }
    if (clb) {
        clb();
    }
};


export const updateClientState = (
    account: Api.Account,
    isInstant = false
): Thunk => (dispatch, getState) => {
    const state = getState();
    const clientState = getClientState(account, state);
    const { revision } = account;

    dispatch(updateDockData(clientState, revision as number, isInstant));

    dispatch(appSliceUpdateClientState(clientState.shipLevel));
};


export const setShipLevelLocal = (
    level: number,
    // eslint-disable-next-line default-param-last
    isInstant = false,
    clb?: () => void
): Thunk => (dispatch, getState) => {
    const state = getState();
    const { secondaryShipLevels, tertiaryShipLevels } = state.app;
    const revision = state.app.account.revision ?? 0;

    const clientState = getClientState(state.app.account, state);
    clientState.shipLevel = level;
    const newSecondaryShipLevel = getNewExtraShipLevel(secondaryShipLevels, level);
    const newTertiaryShipLevel = getNewExtraShipLevel(tertiaryShipLevels, level);

    clientState.secondaryShipLevel = newSecondaryShipLevel.level ?? 0;
    clientState.tertiaryShipLevel = newTertiaryShipLevel.level ?? 0;

    dispatch(updateDockData(clientState, revision, isInstant, clb));

    dispatch(appSliceSetShipLevel(level));
};


export const setWorldRendererState = (enabled: boolean): Thunk => (dispatch) => {
    ClientApi.switchWorldRendererState(enabled);
    dispatch(appSliceSetWorldRendererState(enabled));
};


export const showErrorMessage = (error: Error): Thunk => () => {
    log.error(error);
};


export const updateDockData = (
    clientState: ClientState,
    revision: number,
    isInstant: boolean,
    clb?: () => void
): Thunk => (dispatch, getState) => {
    const state = getState();
    const { actionLevels } = state.app;
    animationIterator.update(clientState, revision, isInstant, actionLevels, dispatch, getState).then(() => {
        if (clb) {
            clb();
        }
    });
};
